import React, { useState, useEffect } from "react";
import SwitchPage from "./SwitchPage";
import { getUserData, setUserData } from "./const";
import useTheme from "../hooks/useTheme"; // Import theme hook

// Import theme system CSS
import "../assets/css/themes/themes.css";

import { createErpSocket } from "../utils/socketIo";
import {
  getErpToken,
  getLastDailyPrice,
  applyRealtimeSessionFromAdminUserSocket,
  getErpTenantBranding,
  getTenantBrandImageSrc,
  cacheTenantBrandingImages,
} from "../erpClient";

function Main() {
  // Initialize theme system
  const { currentTheme, isThemeSystemLoaded } = useTheme();

  const [showLiveRate, setShowLiveRate] = useState(false);
  const [userData, setUserDataState] = useState(getUserData()); // Make userData stateful for real-time updates

  // Rate state variables
  const [liveGoldRate, setLiveGoldRate] = useState(0);
  const [dbGoldRate, setDbGoldRate] = useState(0);
  const [dbGoldRates, setDbGoldRates] = useState([0, 0, 0, 0, 0, 0]); // [24,22,18,18 Rose,9,9 Rose]
  const [masterGoldRate, setMasterGoldRate] = useState(0);

  const [liveSilverRate, setLiveSilverRate] = useState(0);
  const [dbSilverRate, setDbSilverRate] = useState(0);
  const [masterSilverRate, setMasterSilverRate] = useState(0);

  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  const [dbLastUpdateTime, setDbLastUpdateTime] = useState(null);
  const [socket, setSocket] = useState(null);

  // Fetch daily prices from API
  const fetchDailyPrices = async () => {
    try {
      // Daily price endpoint is protected in ERP API.
      // Avoid firing request before auth is available.
      if (!getErpToken()) {
        return;
      }

      const result = await getLastDailyPrice();
      if (result?.success) {
        const price =
          result.data?.daily_price ??
          result.data?.last_entry?.[0] ??
          result.data?.last_entry ??
          result.data?.data ??
          result.data;

        // Update DB rates
        const gold24 = parseFloat(price["24_carat_gold"] || 0);
        const gold22 = parseFloat(price["22_carat_gold"] || 0);
        const gold18 = parseFloat(price["18_carat_gold"] || 0);
        const gold9Raw = price?.["9_carat_gold"];
        const gold9 =
          gold9Raw === undefined ||
          gold9Raw === null ||
          String(gold9Raw).trim() === ""
            ? (gold24 * 9) / 24
            : parseFloat(gold9Raw) || 0;
        const gold18Rose = (() => {
          const roseRaw = price?.["18_carat_rose_gold"];
          if (
            roseRaw === undefined ||
            roseRaw === null ||
            String(roseRaw).trim() === ""
          ) {
            return gold18;
          }
          const parsed = parseFloat(roseRaw);
          return Number.isFinite(parsed) ? parsed : gold18;
        })();
        const gold9Rose = (() => {
          const roseRaw =
            price?.["9_carat_rose_gold"] ?? price?.rose_gold_9;
          if (
            roseRaw === undefined ||
            roseRaw === null ||
            String(roseRaw).trim() === ""
          ) {
            return gold9;
          }
          const parsed = parseFloat(roseRaw);
          return Number.isFinite(parsed) ? parsed : gold9;
        })();
        setDbGoldRate(gold24);
        setDbGoldRates([
          gold24,
          gold22,
          gold18,
          gold18Rose,
          gold9,
          gold9Rose,
        ]);
        setDbSilverRate(parseFloat(price["silver"] ?? price.silver_rate) || 0);
        setDbLastUpdateTime(price.date ? new Date(price.date) : null);

        // If not showing live rates, update master rates
        if (!showLiveRate) {
          setMasterGoldRate([
            gold24,
            gold22,
            gold18,
            gold18Rose,
            gold9,
            gold9Rose,
          ]);
          setMasterSilverRate(
            parseFloat(price["silver"] ?? price.silver_rate) || 0,
          );
          setLastUpdateTime(price.date ? new Date(price.date) : new Date());
        }
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        return;
      }
      console.error("Error fetching daily prices:", error);
    }
  };

  // Fetch live rates
  const fetchLiveRates = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_LIVE_RATE_URL);
      const text = await response.text();

      // Clean and parse the text response
      const cleanText = text.replace(/\r/g, "").replace(/\u0000/g, "");
      const lines = cleanText
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0);

      // Find gold rate (GOLD 999 IMP (AMD) T+1 - line 2148)
      const goldLine = lines.find(
        (line) => line.startsWith("2148") && line.includes("GOLD 999 IMP"),
      );
      if (goldLine) {
        // Find the position of the name end and parse rates
        // Example: 2148   GOLD 999 IMP (AMD) T+1    139769 ...
        // Split by multiple spaces/tabs
        const parts = goldLine.split(/\s+/);

        // Iterate through parts to find the first valid rate > 1000 after the ID
        // Skip the ID (index 0) and Name parts
        let foundRate = false;
        for (let i = 1; i < parts.length; i++) {
          const part = parts[i];
          // Skip parts that are part of the name (non-numeric or mixed)
          // We look for a pure number
          if (/^\d+(\.\d+)?$/.test(part)) {
            const rate = parseFloat(part);
            // Gold rate should be > 1000 to avoid picking up "999" purity
            if (!isNaN(rate) && rate > 1000) {
              setLiveGoldRate((prev) => (prev === rate ? prev : rate));
              //  console.log("Updated Gold Rate (GOLD 999 IMP):", rate);
              foundRate = true;
              break;
            }
          }
        }

        if (!foundRate) {
          //  console.log("Could not parse gold rate from line:", goldLine);
        }
      } else {
        // console.log("Gold line not found in response"); // Debug log
      }

      // Find silver rate (SILVER 999 (AHM) PETI 30kg - line 2174)
      const silverLine = lines.find(
        (line) =>
          line.startsWith("2174") &&
          line.includes("SILVER 999 (AHM) PETI 30kg"),
      );
      if (silverLine) {
        // console.log("Found silver line:", silverLine); // Enable debug log
        // Find the position of "PETI 30kg" and get the part after it
        const petiIndex = silverLine.indexOf("PETI 30kg");
        if (petiIndex !== -1) {
          const afterPeti = silverLine.substring(petiIndex + 10).trim();
          // console.log("Text after PETI 30kg:", afterPeti); // Debug log to see extracted text
          const parts = afterPeti.split(/\s+/);
          // Get the first number after PETI 30kg
          for (const part of parts) {
            // console.log("Checking part:", part); // Debug log each part
            const rate = parseFloat(part);
            if (!isNaN(rate) && rate > 100) {
              // Assuming silver rate will be > 100
              setLiveSilverRate((prev) => (prev === rate ? prev : rate));
              // console.log(
              //   "Updated Silver Rate (SILVER 999 (AHM) PETI 30kg):",
              //   rate
              // );
              break;
            }
          }
        }
      } else {
        // console.log("Silver line not found in response"); // Enable debug log

        // Alternative search if specific line number fails
        // console.log("Trying alternative search for silver...");
        const silverLineAlt = lines.find(
          (line) =>
            line.includes("SILVER 999") && line.includes("(AHM) PETI 30kg"),
        );

        if (silverLineAlt) {
          // console.log("Found silver line (alternative):", silverLineAlt);
          // Find the position of "PETI 30kg" and get the part after it
          const petiIndex = silverLineAlt.indexOf("PETI 30kg");
          if (petiIndex !== -1) {
            const afterPeti = silverLineAlt.substring(petiIndex + 10).trim();
            // console.log("Text after PETI 30kg (alt):", afterPeti);
            const parts = afterPeti.split(/\s+/);
            // Get the first number after PETI 30kg
            for (const part of parts) {
              const rate = parseFloat(part);
              if (!isNaN(rate) && rate > 100) {
                // Assuming silver rate will be > 100
                setLiveSilverRate((prev) => (prev === rate ? prev : rate));
                // console.log("Updated Silver Rate (alternative):", rate);
                break;
              }
            }
          }
        } else {
          // console.log("Alternative silver search also failed");
          // Print first few lines containing "SILVER" for debugging
          // console.log("Available SILVER lines:");
          // lines
          //   .filter((line) => line.includes("SILVER"))
          //   .slice(0, 5)
          //   .forEach((line) => console.log(line));
        }
      }

      // masterGoldRate/masterSilverRate/lastUpdateTime are updated by the
      // rate-switching effect when liveGoldRate/liveSilverRate changes.
    } catch (error) {
      // console.error("Error fetching live rates:", error);
      if (error.response) {
        // console.error("Response status:", error.response.status);
        // console.error("Response data:", error.response.data);
      }
    }
  };

  // Initial fetch of daily prices
  useEffect(() => {
    fetchDailyPrices();
  }, []);

  // Live rates: keep deps separate from DB rates so socket/API updates to
  // dbGoldRates do not re-run the live branch every time (avoids extra renders
  // and possible update loops when combined with frequent live polling).
  useEffect(() => {
    if (!showLiveRate) return;
    setMasterGoldRate(liveGoldRate);
    setMasterSilverRate(liveSilverRate);
    setLastUpdateTime(new Date());
  }, [showLiveRate, liveGoldRate, liveSilverRate]);

  useEffect(() => {
    if (showLiveRate) return;
    setMasterGoldRate(dbGoldRates);
    setMasterSilverRate(dbSilverRate);
    setLastUpdateTime(dbLastUpdateTime || new Date());
  }, [showLiveRate, dbGoldRates, dbSilverRate, dbLastUpdateTime]);

  // Fetch latest DB rates only when toggling to DB view
  useEffect(() => {
    if (!showLiveRate) {
      fetchDailyPrices();
    }
  }, [showLiveRate]);

  // Effect for fetching live rates
  useEffect(() => {
    if (showLiveRate) {
      fetchLiveRates();
      // Fetch rates every second
      const interval = setInterval(fetchLiveRates, 1000);
      return () => clearInterval(interval);
    }
  }, [showLiveRate]);

  // Socket.IO listener for real-time daily price updates
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    // Connection event logging
    newSocket.on("connect", () => {
      // console.log("Main: Connected to socket server");
    });

    newSocket.on("disconnect", (reason) => {
      // console.log("Main: Disconnected from socket server:", reason);
    });

    newSocket.on("connect_error", (error) => {
      console.error("Main: Socket connection error:", error);
    });

    newSocket.on("daily_price_created", (data) => {
      // console.log("Socket daily price data received in Main:", data);
      if (!data?.daily_price) return;

      const price = data.daily_price;
      const gold24 = parseFloat(price["24_carat_gold"] || 0);
      const gold22 = parseFloat(price["22_carat_gold"] || 0);
      const gold18 = parseFloat(price["18_carat_gold"] || 0);
      const gold9Raw = price?.["9_carat_gold"];
      const gold9 =
        gold9Raw === undefined ||
        gold9Raw === null ||
        String(gold9Raw).trim() === ""
          ? (gold24 * 9) / 24
          : parseFloat(gold9Raw) || 0;
      const gold18Rose = (() => {
        const roseRaw = price?.["18_carat_rose_gold"];
        if (
          roseRaw === undefined ||
          roseRaw === null ||
          String(roseRaw).trim() === ""
        ) {
          return gold18;
        }
        const parsed = parseFloat(roseRaw);
        return Number.isFinite(parsed) ? parsed : gold18;
      })();
      const gold9Rose = (() => {
        const roseRaw =
          price?.["9_carat_rose_gold"] ?? price?.rose_gold_9;
        if (
          roseRaw === undefined ||
          roseRaw === null ||
          String(roseRaw).trim() === ""
        ) {
          return gold9;
        }
        const parsed = parseFloat(roseRaw);
        return Number.isFinite(parsed) ? parsed : gold9;
      })();
      const silver = parseFloat(price["silver"] || 0);
      const dateTime = price.date ? new Date(price.date) : new Date();

      // Update DB rates
      setDbGoldRate(gold24);
      setDbGoldRates([
        gold24,
        gold22,
        gold18,
        gold18Rose,
        gold9,
        gold9Rose,
      ]);
      setDbSilverRate(silver);
      setDbLastUpdateTime(dateTime);

      // If not showing live rates, update master rates immediately
      if (!showLiveRate) {
        setMasterGoldRate([
          gold24,
          gold22,
          gold18,
          gold18Rose,
          gold9,
          gold9Rose,
        ]);
        setMasterSilverRate(silver);
        setLastUpdateTime(dateTime);
      }
    });

    // Listen for employee permission updates for real-time permission changes
    newSocket.on("employee_permission_updated", (data) => {
      // ERP sessions often have no legacy `userData`; sync via auth store + /auth/me.
      applyRealtimeSessionFromAdminUserSocket(data);

      if (!data?.employee) return;

      const updatedEmployee = {
        ua_id: data.employee.id || data.employee.ua_id,
        name: data.employee.name || "",
        phone: data.employee.phone || "",
        type: parseInt(data.employee.usertype || data.employee.type) || 0,
        status: parseInt(data.employee.status) || 0,
        email: data.employee.email || "",
      };

      // Check if the updated employee is the currently logged-in user
      if (
        updatedEmployee.ua_id === userData?.id ||
        updatedEmployee.ua_id === userData?.ua_id
      ) {
        // Update logged-in user's data for real-time permission changes
        const updatedUserData = {
          ...userData,
          usertype: updatedEmployee.type,
          status: updatedEmployee.status,
        };

        setUserDataState(updatedUserData);

        // Also update the persistent storage so changes persist after page refresh
        try {
          // Get current user data and update only permission fields
          const currentUserData = getUserData();
          if (currentUserData) {
            const updatedStorageData = {
              ...currentUserData,
              usertype: updatedEmployee.type,
              status: updatedEmployee.status,
            };
            setUserData(updatedStorageData);
          }
        } catch (error) {
          console.error("Error updating user data in storage:", error);
        }

        // console.log("Main: User permissions updated in real-time", updatedUserData);
      }
    });

    newSocket.on("admin_user_updated", (data) => {
      applyRealtimeSessionFromAdminUserSocket(data);
    });

    return () => {
      newSocket.off("daily_price_created");
      newSocket.off("employee_permission_updated");
      newSocket.off("admin_user_updated");
      newSocket.disconnect();
    };
  }, [showLiveRate]);

  // Handler for live rate toggle
  const handleLiveRateToggle = (isChecked) => {
    setShowLiveRate(isChecked);
  };

  useEffect(() => {
    const handleBranchOrAuthUpdate = () => {
      setUserDataState(getUserData());
      fetchDailyPrices();
    };
    window.addEventListener("branchChanged", handleBranchOrAuthUpdate);
    window.addEventListener("permissionsUpdated", handleBranchOrAuthUpdate);
    window.addEventListener("erpAuthUpdated", handleBranchOrAuthUpdate);
    return () => {
      window.removeEventListener("branchChanged", handleBranchOrAuthUpdate);
      window.removeEventListener("permissionsUpdated", handleBranchOrAuthUpdate);
      window.removeEventListener("erpAuthUpdated", handleBranchOrAuthUpdate);
    };
  }, []);

  // Apply the tenant favicon saved from verify_otp. A missing upload keeps index.html's icon.
  useEffect(() => {
    const applyFavicon = () => {
      const faviconUrl = getTenantBrandImageSrc("favicon");
      if (!faviconUrl) return;
      document
        .querySelectorAll(
          "link[rel~='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']",
        )
        .forEach((linkEl) => {
          linkEl.href = faviconUrl;
        });
    };

    applyFavicon();
    void cacheTenantBrandingImages(getErpTenantBranding());

    const handleBrandingUpdate = () => applyFavicon();
    window.addEventListener("erpTenantBrandingUpdated", handleBrandingUpdate);
    window.addEventListener("erpAuthUpdated", handleBrandingUpdate);
    return () => {
      window.removeEventListener("erpTenantBrandingUpdated", handleBrandingUpdate);
      window.removeEventListener("erpAuthUpdated", handleBrandingUpdate);
    };
  }, []);

  return (
    <>
      <SwitchPage
        masterGoldRate={masterGoldRate}
        masterSilverRate={masterSilverRate}
        showLiveRate={showLiveRate}
        onLiveRateToggle={handleLiveRateToggle}
        lastUpdateTime={lastUpdateTime}
      />
    </>
  );
}

export default Main;
